import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, RefreshCw, Search } from "lucide-react";

const API = "/api";

const TAG_COLORS = {
  QR_RUNTIME_START: "text-sky-300",
  BUTTON_ACTION_CLICKED: "text-sky-400",
  MESSAGE_SESSION_CREATED: "text-cyan-300",
  MESSAGE_SAVED: "text-cyan-300",
  OWNER_REPLY_SENT: "text-violet-300",
  SCANNER_REPLY_RECEIVED: "text-cyan-200",
  CALL_SESSION_CREATED: "text-emerald-300",
  OWNER_ACCEPTED_CALL: "text-emerald-200",
  OWNER_LINK_OPENED: "text-indigo-300",
  OWNER_CONTACT_RESOLUTION: "text-yellow-300",
  EMERGENCY_CONTACT_RESOLUTION: "text-red-300",
  NOTIFICATION_ATTEMPT: "text-blue-300",
  NOTIFICATION_PROVIDER_RESPONSE: "text-blue-200",
  NOTIFICATION_SENT: "text-green-300",
  NOTIFICATION_FAILED: "text-red-300",
  NOTIFICATION_SKIPPED: "text-amber-300",
  NOTIFICATION_DELIVERY_STATUS: "text-emerald-300",
  WS_CONNECT: "text-purple-300",
  WS_DISCONNECT: "text-purple-400",
  OFFER: "text-fuchsia-300",
  ANSWER: "text-fuchsia-300",
  ICE: "text-fuchsia-400",
  HANGUP: "text-red-400",
  STATE: "text-pink-300",
  WEBRTC_CONNECTION_STATE: "text-pink-200",
  AUDIO_TRACK_STATUS: "text-rose-300",
  CALL_CONNECTED_CONFIRMED: "text-green-300",
  CALL_FAILED: "text-red-400",
};

const AdminRuntimeLogs = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/debug/runtime-logs?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(res.data.events || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetch();
    const interval = setInterval(fetch, 5000);
    return () => clearInterval(interval);
  }, [fetch]);

  const visible = events.filter((ev) => {
    if (!q.trim()) return true;
    const qq = q.toLowerCase();
    return (
      (ev.tag || "").toLowerCase().includes(qq) ||
      (ev.session_id || "").toLowerCase().includes(qq) ||
      (ev.qr_token_prefix || "").toLowerCase().includes(qq) ||
      JSON.stringify(ev.data || {}).toLowerCase().includes(qq)
    );
  });

  return (
    <div
      className="min-h-screen w-full max-w-full overflow-x-hidden bg-[#071B2F] text-slate-100 p-4"
      data-testid="admin-runtime-logs"
    >
      <div className="max-w-5xl mx-auto min-w-0">
        <div className="flex items-center justify-between mb-3">
          <Link to="/admin" className="text-cyan-400 text-sm flex items-center gap-1" data-testid="back-admin">
            <ArrowLeft size={14} /> Admin
          </Link>
          <button
            onClick={fetch}
            disabled={loading}
            className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 flex items-center gap-1 disabled:opacity-50"
            data-testid="runtime-refresh-btn"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refrescar
          </button>
        </div>

        <h1 className="text-xl font-bold mb-1 text-white">Diagnóstico técnico en tiempo real</h1>
        <p className="text-xs text-slate-400 mb-4">
          Trazas internas del flujo de mensajes y llamadas, con los datos redactados.
          Se refresca cada 5 s y se conserva 3 días. Es una herramienta de soporte:
          no son métricas de negocio.
        </p>

        <div className="flex flex-wrap gap-2 mb-4" data-testid="runtime-counts">
          {Object.entries(counts).map(([tag, n]) => (
            <div
              key={tag}
              className="min-w-0 max-w-full rounded-lg bg-white/5 border border-white/10 p-2 font-mono text-[11px] break-all"
              data-testid={`count-${tag}`}
            >
              <span className={`font-bold ${TAG_COLORS[tag] || "text-slate-200"}`}>[{tag}]</span>
              <span className="text-slate-300 ml-2">{n}</span>
            </div>
          ))}
          {Object.keys(counts).length === 0 && (
            <div className="col-span-full text-slate-500 text-xs">Sin eventos runtime todavia.</div>
          )}
        </div>

        <div className="flex items-center gap-2 mb-3">
          <Search size={14} className="text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filtrar por tag, session, token o data..."
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm flex-1 outline-none focus:border-cyan-500/60"
            data-testid="runtime-filter"
          />
          <span className="text-xs text-slate-500" data-testid="runtime-visible-count">
            {visible.length}/{events.length}
          </span>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/30 overflow-hidden">
          <div className="max-h-[70vh] w-full max-w-full overflow-auto font-mono text-[11px]" data-testid="runtime-events-list">
            {visible.map((ev, i) => (
              <div
                key={i}
                className="min-w-0 px-3 py-1 border-b border-white/5 flex flex-wrap gap-2 hover:bg-white/5"
                data-testid={`runtime-event-${ev.tag}`}
              >
                <span className="text-slate-500">{ev.t?.slice(11, 19)}</span>
                <span className={`font-bold ${TAG_COLORS[ev.tag] || "text-slate-200"}`}>[{ev.tag}]</span>
                {ev.session_id && (
                  <span className="text-cyan-300" title={ev.session_id}>
                    ses:{ev.session_id.slice(0, 8)}
                  </span>
                )}
                {ev.qr_token_prefix && <span className="text-indigo-300">qr:{ev.qr_token_prefix}</span>}
                {ev.user_id_prefix && <span className="text-violet-300">u:{ev.user_id_prefix}</span>}
                {ev.data && Object.keys(ev.data).length > 0 && (
                  <span className="min-w-0 text-slate-300 truncate flex-1 break-all" title={JSON.stringify(ev.data)}>
                    {JSON.stringify(ev.data)}
                  </span>
                )}
              </div>
            ))}
            {visible.length === 0 && (
              <div className="p-4 text-slate-500 text-xs">Sin eventos que coincidan.</div>
            )}
          </div>
        </div>

        <p className="text-[10px] text-slate-600 text-center pt-4">
          Todos los datos estan redactados. Retencion 3 dias.
        </p>
      </div>
    </div>
  );
};

export default AdminRuntimeLogs;
